import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyName, useTokens } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance } from '../utils/luminance';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { CTA_SOLID, CTA_SURFACE_HOVER, CtaStyles, ctaOverLight } from './_cta';

// Defaults to the SECOND gallery photo (index 1), not index 0. Index 0 is
// typically the hero's featured image (and on some properties the logo), so
// binding there duplicates the hero / shows the logo. A later gallery image is
// a distinct, standout photo, and the generator below refines it at build time.
const imageDef: SlotGroupDefinition = {
  label: 'Overview Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select one standout photo that makes the property feel special — a clubhouse, pool, courtyard, rooftop, lounge, fitness center, or striking building exterior. Never pick a logo, wordmark, branding graphic, sign, map, floor plan, or any text/graphic-only image — choose a real photograph. Also avoid mundane utility spaces such as closets, laundry rooms / washer-dryer, bathrooms, or empty unit interiors.',
    context:
      'Single showcase image beside the property overview copy on the home page. Do not reuse the hero / featured image shown elsewhere on the page — pick a different, standout amenity or community space.',
    sourceCollection: 'photo_gallery',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Gallery CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// Number of identical marquee tiles. Over-repeats on purpose so the visible
// path is always covered while scrolling (glyphs past the path end don't render).
const MARQUEE_TILES = 10;
// Scroll speed in SVG user units/sec. Higher = faster (was ~54 u/s at 25%/6s).
const MARQUEE_PX_PER_SEC = 72;

const marqueeDef: SlotGroupDefinition = {
  label: 'Scrolling Marquee',
  fields: {
    text: { label: 'Text', type: 'text', defaultField: 'property_name' },
  },
};

export function PropertyOverview() {
  const image = useSlotGroup('propertyoverview.image', imageDef);
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const marquee = useSlotGroup('propertyoverview.marquee', marqueeDef);

  useRevealStyles();
  const containerRef = useScrollReveal<HTMLDivElement>();

  // When the theme's on-primary text color is dark, the property has a light
  // primary — a light-primary title reads too faint, so fall back to solid
  // black. Otherwise keep the branded primary color.
  const tokens = useTokens();
  const titleColor =
    relativeLuminance(tokens?.color?.textOnPrimary) < 0.5
      ? '#000000'
      : 'var(--vibe-color-primary)';

  // Decorative curved marquee: "flourish at <editable text>" repeated.
  // Built from N identical tiles so the string is exactly periodic — a
  // prerequisite for a seamless scroll (see the measured loop below).
  const propertyName = usePropertyName() || 'Verso';
  const marqueePhrase = `flourish at ${marquee.text || propertyName}`;
  const marqueeTile = `${marqueePhrase} ✳ `;
  const marqueeText = marqueeTile.repeat(MARQUEE_TILES);

  // Seamless loop: scroll `startOffset` by exactly one tile's width so the
  // glyph landing on every point of the path is identical at loop start and
  // end — no jump. The tile width can't be known statically (it depends on the
  // font, the font size, and the editable text), so measure it after render
  // and derive the exact offset + a duration that keeps a constant speed.
  const textPathRef = useRef<SVGTextPathElement>(null);
  const [loop, setLoop] = useState<{ from: number; dur: number } | null>(null);

  useEffect(() => {
    const el = textPathRef.current;
    if (!el || typeof el.getComputedTextLength !== 'function') return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      try {
        const tile = el.getComputedTextLength() / MARQUEE_TILES;
        if (!Number.isFinite(tile) || tile <= 0) return;
        setLoop({ from: -tile, dur: tile / MARQUEE_PX_PER_SEC });
      } catch {
        /* getComputedTextLength can throw pre-layout — ignore, retry on fonts */
      }
    };
    measure();
    // Re-measure once webfonts settle, since the tile width changes with them.
    const fonts = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready?.then) fonts.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [marqueeText]);

  // Before measurement (SSR / first paint) fall back to a percentage-based
  // scroll so motion starts immediately; swap to the seamless values once known.
  const marqueeFrom = loop ? loop.from : '-25%';
  const marqueeTo = loop ? 0 : '0%';
  const marqueeDur = loop ? `${loop.dur}s` : '4.5s';

  return (
    <section className="pt-12 md:pt-20 pb-0 bg-[var(--vibe-color-background)] overflow-hidden">
      <CtaStyles />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div
          ref={containerRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-16 items-center"
        >
          {/* Arched image */}
          <div className="overview-arch-frame mx-auto w-full max-w-[16rem] sm:max-w-sm md:max-w-md">
            <EditableSlotGroup groupPath="propertyoverview.image" definition={imageDef}>
              <div className="overview-arch-clip aspect-[8/9]">
                {image.image ? (
                  <VibesitesImage
                    url={image.image}
                    alt={image.alt || ''}
                    sizes="(min-width: 768px) 28rem, 100vw"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[color-mix(in_srgb,var(--vibe-color-primary)_20%,var(--vibe-color-background))]" />
                )}
              </div>
            </EditableSlotGroup>
          </div>

          {/* Copy */}
          <div className="text-center md:text-left">
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="font-heading text-[clamp(1.75rem,6vw,2.25rem)] sm:text-5xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-[1.1] sm:leading-[1.05] pr-2 sm:pr-4 md:pr-6 [overflow-wrap:break-word] [text-wrap:balance]"
              style={{ color: titleColor }}
            />

            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="font-body text-base sm:text-lg text-[var(--vibe-color-text)]/75 max-w-xl mx-auto md:mx-0 leading-relaxed [text-wrap:pretty] [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/gallery'}
                target={ctaLink._target || '_self'}
                className={`mt-8 sm:mt-10 ${CTA_SOLID} ${CTA_SURFACE_HOVER}`}
                style={ctaOverLight}
              >
                {ctaLink.label || 'View Gallery'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* Curved decorative marquee */}
      <EditableSlotGroup groupPath="propertyoverview.marquee" definition={marqueeDef}>
        <div className="mt-10 sm:mt-12 md:mt-20" aria-hidden="true">
          <svg
            viewBox="0 0 1200 150"
            className="w-full"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <path id="overview-curve" d="M -20,40 Q 600,180 1220,40" fill="none" />
            </defs>
            <text
              className="overview-marquee-text"
              textAnchor="start"
              style={{ fill: titleColor }}
            >
              <textPath ref={textPathRef} href="#overview-curve" startOffset={marqueeFrom}>
                {marqueeText}
                <animate
                  key={loop ? `m:${loop.from}:${loop.dur}` : 'fallback'}
                  attributeName="startOffset"
                  from={marqueeFrom}
                  to={marqueeTo}
                  dur={marqueeDur}
                  repeatCount="indefinite"
                  calcMode="linear"
                />
              </textPath>
            </text>
          </svg>
        </div>
      </EditableSlotGroup>

      <style>{`
        /* CTA size + hover come from the canonical .v-cta / .v-cta--surface-hover
           in _cta.tsx, shared by every CTA in the theme. */
        .overview-arch-frame {
          border: 2px solid var(--vibe-color-primary);
          border-radius: 50% 50% 6px 6px / 42% 42% 6px 6px;
          padding: 10px;
        }
        @media (min-width: 640px) {
          .overview-arch-frame {
            padding: 14px;
          }
        }
        .overview-arch-clip {
          border-radius: 50% 50% 3px 3px / 40% 40% 3px 3px;
          overflow: hidden;
        }
        .overview-marquee-text {
          font-family: var(--vibe-font-heading, serif);
          font-size: 42px;
          font-weight: 600;
          letter-spacing: 0.01em;
        }
        @media (max-width: 767px) {
          .overview-marquee-text {
            font-size: 68px;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.image': imageDef,
    'propertyoverview.cta_link': ctaLinkDef,
    'propertyoverview.marquee': marqueeDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
